'use client';

import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cva } from 'class-variance-authority';
import { cn } from '../utils';

const switchVariants = cva('', {
  variants: {
    size: {
      sm: 'h-5 w-9',
      default: 'h-[24px] w-[44px]',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});
const switchThumbVariants = cva('', {
  variants: {
    size: {
      sm: 'h-4 w-4 data-[state=checked]:translate-x-4',
      default: 'h-5 w-5 data-[state=checked]:translate-x-5',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export type SwitchProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitives.Root
> & {
  size?: 'sm' | 'default';
};

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, size = 'default', ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50',
      // Checked
      `data-[state=checked]:bg-brand-600 data-[state=unchecked]:bg-gray-100`,
      // Focus
      `focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-brand-300`,
      // Dark
      `dark:data-[state=checked]:bg-gray-50 dark:data-[state=unchecked]:bg-gray-800 dark:focus-visible:ring-gray-300 dark:focus-visible:ring-offset-gray-950`,
      switchVariants({ size }),
      className,
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none block rounded-full bg-white shadow-sm ring-0 transition-transform data-[state=unchecked]:translate-x-0 dark:bg-gray-950',
        switchThumbVariants({ size }),
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
